import type {
  BrowserCommand,
  BrowserDisplaySession,
  BrowserRenderMode,
  BrowserSessionSnapshot,
  BrowserStreamEvent,
  BrowserTabSnapshot,
  BrowserViewport,
  DesktopNativeBrowserResult,
  DevTunnelRecord,
  EnvironmentId,
  EventId,
  ThreadId,
} from "@cmmd-forge/contracts";
import type { BrowserThreadState } from "~/browserStateStore";
import { isBrowserFrameFresh, isFreshBrowserCapture } from "./BrowserPreviewPanel.capture";
import { resolveBrowserPreviewVm } from "./BrowserPreviewPanel.previewVm";
import { browserDisplayUrl, isBlankBrowserUrl } from "./BrowserPreviewPanel.url";
import {
  browserTabDisplayLabel,
  duplicateTabLabelSet,
  resolveBrowserActiveTabViewState,
  resolveBrowserBusyState,
  resolveBrowserInteractiveSurfaceViewport,
  resolveBrowserLiveDisplayState,
  selectBrowserDisplayFrameSource,
} from "./BrowserPreviewPanel.viewModel";
import {
  resolveBrowserPresentation,
  type BrowserClientCapability,
} from "@cmmd-forge/shared/browserPresentation";
import {
  findRestorableHistoryTab,
  isRecoverableDevTunnelFailure,
  recoverableLocalPreviewDisplayUrl,
  selectPreferredPreviewTab,
  shouldRestoreBrowserSessionFromThreadHistory,
  visibleBrowserTabs,
} from "./BrowserPreviewPanel.recovery";

export {
  browserErrorLooksLikeRuntimeTimeout,
  remoteWebRtcFailureReasonForMessage,
  resolveBrowserPreviewVm,
  resolveRemoteWebRtcInputTimeoutRecoveryDisplay,
  type BrowserPreviewStatus,
} from "./BrowserPreviewPanel.previewVm";

export {
  BrowserAttachTimeoutError,
  buildBrowserOptionsMenuItems,
  browserSessionOpenAttemptCount,
  browserSessionUnavailableMessage,
  buildInvalidBrowserAddressMessage,
  hasDevTunnelAccessToken,
  hasReusableDevTunnelAccess,
  isBrowserAttachTimeoutError,
  isRecoverableBrowserSessionCommandError,
  mergeDevTunnelRecords,
  preserveLocalPreviewTabsForMaintenanceCommand,
  resolveBrowserAddressSubmitAction,
  resolveBrowserDevServerPreviewAction,
  resolveBrowserRestartUrl,
  resolveDevServerDisplayPreviewUrl,
  shouldRestartLocalPreviewAddressCommand,
  shouldStartBrowserSession,
  shouldOpenBrowserSession,
  shouldRetryBrowserSessionOpenError,
  shouldRetryHiddenLocalPreviewSessionOpen,
  shouldUseDevTunnelForPreview,
  upsertDevTunnelRecord,
  withBrowserOperationTimeout,
} from "./BrowserPreviewPanel.session";

export {
  browserCommandCanChangeActiveAddress,
  browserCommandHasVisiblePendingState,
  browserCommandTabId,
  browserCommandTimeoutMs,
  browserCommandUsesInteractiveInput,
  buildBrowserAddressCommand,
  buildBrowserPreviewSubscriptionInput,
  resolveBrowserCommandExecutionPlan,
  resolveBrowserCommandRuntimeAvailability,
  nativeBrowserCanHandleCommand,
  resolveBrowserCommandRoute,
  resolveVisiblePendingBrowserCommand,
  shouldRefreshBrowserSessionAfterCommand,
  shouldRestartBrowserSessionForAddressRecovery,
  type BrowserAddressCommand,
} from "./BrowserPreviewPanel.commands";

export {
  browserPreviewUrlsMatch,
  buildBrowserScreenshotAttachmentMetadata,
  hasUsableBrowserScreenshotDataUrl,
  isBrowserFrameFresh,
  isFreshBrowserCapture,
  resolveBrowserAnnotationToggleAction,
  resolveBrowserCommentSubmitAction,
  resolveBrowserViewportSyncDecision,
} from "./BrowserPreviewPanel.capture";

export {
  browserSessionHasAddressUrl,
  browserSessionHasDifferentDevTunnelForLocalPreview,
  browserSessionHasRecoverableLocalPreviewPort,
  browserSessionHasVisibleAddressIntent,
  browserSessionHasVisibleAddressUrl,
  deriveRestorableThreadBrowserUrl,
  findRestorableHistoryTab,
  isExpiredDevTunnelTab,
  isRecoverableDevTunnelFailure,
  isRecoverableLocalPreviewFailure,
  localPreviewSessionAttemptCount,
  recoverableLocalPreviewDisplayUrl,
  selectPreferredPreviewTab,
  shouldRecoverLocalPreviewBrowserError,
  shouldRestoreBrowserSessionFromThreadHistory,
  shouldRetryLocalPreviewSessionStartup,
  visibleBrowserTabs,
} from "./BrowserPreviewPanel.recovery";

export {
  browserDisplayUrl,
  devTunnelRecordForUrl,
  isBlankBrowserUrl,
  isDevTunnelPreviewUrl,
  isLocalPreviewBrowserUrl,
  readDevTunnelMetadata,
  resolveBrowserRenderMode,
} from "./BrowserPreviewPanel.url";

export {
  formatRemoteWebRtcDisplayError,
  normalizeBrowserDisplayForPreview,
  remoteWebRtcBrowserSurfaceInstanceKey,
  shouldAttachRemoteWebRtcDisplay,
  staleRemoteWebRtcConnectingDisplayFailure,
} from "./BrowserPreviewPanel.remoteWebRtcDisplay";

export {
  BROWSER_PREVIEW_TERMINAL_ID,
  buildPortAwareDevServerCommand,
  pickDevServerScript,
  resolveBrowserDevServerLaunchCardVm,
  resolveDevServerLaunchPlan,
  resolveDevServerRelaunchTargetUrl,
  resolveRunningDevServerPreviewLaunch,
  scoreDevServerScript,
  shouldReleaseDevRuntimeBeforeLaunch,
  shouldRunDevServerLaunch,
  shouldShowDevServerLaunchCard,
} from "./BrowserPreviewPanel.devServerCommand";

export {
  devTunnelUrlForDisplayUrl,
  resolveBrowserAccessTarget,
} from "./BrowserPreviewPanel.accessTarget";

export {
  browserRememberedAddress,
  browserSessionDisplayName,
  browserSessionSubtitle,
  browserTabDisplayLabel,
  browserTabDisplaySecondaryLabel,
  buildBrowserCommentPrompt,
  duplicateTabLabelSet,
  latestObservedDevServerPreviewUrl,
  resolveBrowserActiveTabViewState,
  resolveBrowserBusyState,
  resolveBrowserConsoleSummary,
  resolveBrowserInteractiveSurfaceViewport,
  resolveBrowserLiveDisplayState,
  resolveBrowserPreviewStateChromeVm,
  selectBrowserDisplayFrameSource,
} from "./BrowserPreviewPanel.viewModel";

export {
  latestTerminalDevServerPreviewUrl,
  localDevServerPreviewPort,
} from "~/lib/devServerPreviewUrl";

export interface BrowserConsoleSummary {
  readonly count: number;
  readonly highestLevel: string | null;
  readonly latestMessage: string | null;
}

export interface BrowserPreviewStateChromeVm {
  readonly isProblem: boolean;
  readonly isConnecting: boolean;
  readonly icon: "alert" | "loader" | "activity" | "globe";
  readonly showActions: boolean;
  readonly showRefresh: boolean;
  readonly showOpenExternal: boolean;
}

export interface BrowserDisplayFrameSource {
  readonly dataUrl: string | undefined;
  readonly url: string | undefined;
  readonly capturedAt: string | undefined;
  readonly viewport: BrowserViewport | undefined;
}

export interface BrowserNativeSurfaceResultState {
  readonly addressValue: string | null;
  readonly tabUpdatedEvent: BrowserStreamEvent | null;
}

export interface BrowserNativeSurfaceSessionState {
  readonly baseSession: BrowserSessionSnapshot;
  readonly shouldSeedThreadSession: boolean;
}

export interface BrowserBusyState {
  readonly commandBusy: boolean;
  readonly actionBusy: boolean;
  readonly busy: boolean;
  readonly addressBusy: boolean;
}

export interface BrowserLiveDisplayState {
  readonly browserDisplay: BrowserDisplaySession | undefined;
  readonly isAttached: boolean;
}

export interface BrowserPreviewObservedText {
  readonly text: string;
  readonly observedAt?: string | undefined;
}

export type BrowserHistoryRestoreDecision =
  | {
      readonly type: "activate-tab";
      readonly attemptKey: string;
      readonly tabId: string;
    }
  | {
      readonly type: "restore-session";
      readonly attemptKey: string;
      readonly restoreUrl: string;
    }
  | {
      readonly type: "none";
    };

/** Write previewTabId to the URL only when the active tab id actually changed. */
export function shouldSyncPreviewTabIdToUrl(input: {
  readonly lastSyncedTabId: string | null;
  readonly routeTabId?: string | null | undefined;
  readonly activeTabId: string | null | undefined;
}): input is typeof input & { readonly activeTabId: string } {
  return Boolean(
    input.activeTabId &&
    (input.lastSyncedTabId !== input.activeTabId || input.routeTabId !== input.activeTabId),
  );
}

/**
 * Apply URL-owned tab selection when the route tab id changes (deep link / back-forward),
 * not when the user activates a different tab in the panel (URL catches up separately).
 */
export function shouldActivateBrowserTabFromRoute(input: {
  readonly routeTabId: string | undefined;
  readonly knownTabIds: readonly string[];
}): input is typeof input & { readonly routeTabId: string } {
  return Boolean(input.routeTabId && input.knownTabIds.includes(input.routeTabId));
}

export function deriveBrowserNativeSurfaceSessionState(input: {
  readonly latestSession: BrowserSessionSnapshot | null | undefined;
  readonly currentThreadSession: BrowserSessionSnapshot | null | undefined;
}): BrowserNativeSurfaceSessionState | null {
  if (!input.latestSession?.browserId || !input.latestSession.sessionId) {
    return null;
  }

  const baseSession = input.currentThreadSession ?? input.latestSession;
  if (!baseSession.browserId || !baseSession.sessionId) {
    return null;
  }

  return {
    baseSession,
    shouldSeedThreadSession: !input.currentThreadSession,
  };
}

export function deriveBrowserHistoryRestoreDecision(input: {
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
  readonly restoreUrl: string | null;
  readonly session: BrowserSessionSnapshot | null;
  readonly routeTabId: string | null | undefined;
}): BrowserHistoryRestoreDecision {
  if (!input.restoreUrl) {
    return { type: "none" };
  }

  const currentSelection = input.session
    ? selectPreferredPreviewTab({
        tabs: input.session.tabs,
        routeTabId: input.routeTabId ?? null,
        activeTabId: input.session.activeTabId,
      })
    : null;
  const restorableHistoryTab = input.session
    ? findRestorableHistoryTab({
        session: input.session,
        restoreUrl: input.restoreUrl,
      })
    : null;

  if (
    input.session &&
    restorableHistoryTab &&
    isBlankBrowserUrl(currentSelection?.url) &&
    input.session.activeTabId !== restorableHistoryTab.tabId
  ) {
    return {
      type: "activate-tab",
      attemptKey: `${input.environmentId}:${input.threadId}:${input.restoreUrl}:activate:${restorableHistoryTab.tabId}`,
      tabId: restorableHistoryTab.tabId,
    };
  }

  if (
    shouldRestoreBrowserSessionFromThreadHistory({
      restoreUrl: input.restoreUrl,
      session: input.session,
    })
  ) {
    return {
      type: "restore-session",
      attemptKey: `${input.environmentId}:${input.threadId}:${input.restoreUrl}`,
      restoreUrl: input.restoreUrl,
    };
  }

  return { type: "none" };
}

export function deriveBrowserNativeSurfaceResultState(input: {
  readonly result: DesktopNativeBrowserResult;
  readonly baseSession: BrowserSessionSnapshot | null | undefined;
  readonly activeTab: BrowserTabSnapshot | null | undefined;
  readonly addressInputFocused: boolean;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
  readonly eventId: EventId;
  readonly createdAt: string;
}): BrowserNativeSurfaceResultState {
  const { baseSession, result } = input;
  const addressValue =
    result.url && !input.addressInputFocused
      ? (browserDisplayUrl(result.url, input.devTunnelRecords) ?? result.url)
      : null;

  if (!baseSession?.browserId || !baseSession.sessionId) {
    return {
      addressValue,
      tabUpdatedEvent: null,
    };
  }

  const resultActiveTab =
    input.activeTab ??
    baseSession.tabs.find((tab) => tab.tabId === baseSession.activeTabId) ??
    baseSession.tabs[0] ??
    null;

  if (
    !resultActiveTab ||
    !(
      (result.url && result.url !== resultActiveTab.url) ||
      (result.title && result.title !== resultActiveTab.title)
    )
  ) {
    return {
      addressValue,
      tabUpdatedEvent: null,
    };
  }

  return {
    addressValue,
    tabUpdatedEvent: {
      eventId: input.eventId,
      browserId: baseSession.browserId,
      environmentId: input.environmentId,
      threadId: input.threadId,
      userId: baseSession.userId,
      agentId: baseSession.agentId,
      createdAt: input.createdAt,
      type: "tab.updated",
      payload: {
        sessionId: baseSession.sessionId,
        tab: {
          ...resultActiveTab,
          ...(result.url ? { url: result.url } : {}),
          ...(result.title ? { title: result.title } : {}),
          updatedAt: input.createdAt,
        },
      },
    },
  };
}

interface ActiveBrowserPreviewArtifactsInput {
  readonly activeTab: BrowserTabSnapshot | null;
  readonly browserThreadState: BrowserThreadState | null | undefined;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
  readonly liveDisplayIsAttached: boolean;
  readonly session: BrowserSessionSnapshot | null;
}

function browserThreadStateValueForActiveTab<T>(
  activeTab: BrowserTabSnapshot | null,
  valuesByTabId: Record<string, T> | undefined,
): T | undefined {
  return activeTab ? valuesByTabId?.[activeTab.tabId] : undefined;
}

function activeTabDisplayUrlFromState(
  activeTabUrl: string | null,
  devTunnelRecords: ReadonlyArray<DevTunnelRecord>,
): string | null {
  return activeTabUrl ? (browserDisplayUrl(activeTabUrl, devTunnelRecords) ?? activeTabUrl) : null;
}

function activeBrowserPreviewStoreArtifacts(
  activeTab: BrowserTabSnapshot | null,
  browserThreadState: BrowserThreadState | null | undefined,
) {
  return {
    activeConsoleLogs: browserThreadStateValueForActiveTab(
      activeTab,
      browserThreadState?.lastConsoleLogsByTabId,
    ),
    activeConsoleLogsMeta: browserThreadStateValueForActiveTab(
      activeTab,
      browserThreadState?.lastConsoleLogsMetaByTabId,
    ),
    activeFrame: browserThreadStateValueForActiveTab(
      activeTab,
      browserThreadState?.lastFrameByTabId,
    ),
    activeFrameMeta: browserThreadStateValueForActiveTab(
      activeTab,
      browserThreadState?.lastFrameMetaByTabId,
    ),
    activeScreenshot: browserThreadStateValueForActiveTab(
      activeTab,
      browserThreadState?.lastScreenshotDataUrlByTabId,
    ),
    activeScreenshotMeta: browserThreadStateValueForActiveTab(
      activeTab,
      browserThreadState?.lastScreenshotMetaByTabId,
    ),
    activeSnapshotText: browserThreadStateValueForActiveTab(
      activeTab,
      browserThreadState?.lastSnapshotContentByTabId,
    ),
  };
}

function activeBrowserFrameIsFresh(input: {
  readonly activeFrame: string | undefined;
  readonly activeFrameMeta: BrowserThreadState["lastFrameMetaByTabId"][string] | undefined;
  readonly activeTab: BrowserTabSnapshot | null;
  readonly session: BrowserSessionSnapshot | null;
}): boolean {
  const { activeFrame, activeFrameMeta, activeTab, session } = input;
  return Boolean(
    activeFrame &&
    activeTab?.url &&
    isBrowserFrameFresh({
      tabUrl: activeTab.url,
      frameUrl: activeFrameMeta?.url,
      frameCapturedAt: activeFrameMeta?.capturedAt,
      requestedAt: activeTab.updatedAt,
      sessionViewport: session?.viewport,
      frameViewport: activeFrameMeta?.viewport,
    }),
  );
}

function activeBrowserScreenshotIsFresh(input: {
  readonly activeScreenshot: string | undefined;
  readonly activeScreenshotMeta:
    | BrowserThreadState["lastScreenshotMetaByTabId"][string]
    | undefined;
  readonly activeTab: BrowserTabSnapshot | null;
  readonly session: BrowserSessionSnapshot | null;
}): boolean {
  const { activeScreenshot, activeScreenshotMeta, activeTab, session } = input;
  return Boolean(
    activeScreenshot &&
    activeTab?.url &&
    isFreshBrowserCapture({
      tabUrl: activeTab.url,
      captureId: activeScreenshotMeta?.captureId,
      expectedCaptureId: activeTab.lastCaptureId,
      captureUrl: activeScreenshotMeta?.url,
      capturedAt: activeScreenshotMeta?.capturedAt,
      requestedAt: activeTab.updatedAt,
      sessionViewport: session?.viewport,
      captureViewport: activeScreenshotMeta?.viewport,
    }),
  );
}

function activeTabHasVisiblePreview(input: {
  readonly activeFrameIsFresh: boolean;
  readonly activeScreenshotIsFresh: boolean;
  readonly activeTab: BrowserTabSnapshot | null;
  readonly activeTabHasRecoverableDevTunnelFailure: boolean;
  readonly liveDisplayIsAttached: boolean;
}): boolean {
  return Boolean(
    input.activeTab &&
    !input.activeTabHasRecoverableDevTunnelFailure &&
    (input.liveDisplayIsAttached || input.activeFrameIsFresh || input.activeScreenshotIsFresh),
  );
}

export function activeBrowserPreviewArtifacts(input: ActiveBrowserPreviewArtifactsInput) {
  const { activeTab, browserThreadState, devTunnelRecords, liveDisplayIsAttached, session } = input;
  const { activeTabUrl, activeRenderMode, shouldPreferBrowserCapture } =
    resolveBrowserActiveTabViewState({ activeTab });
  const activeTabDisplayUrl = activeTabDisplayUrlFromState(activeTabUrl, devTunnelRecords);
  const storeArtifacts = activeBrowserPreviewStoreArtifacts(activeTab, browserThreadState);
  const activeTabHasRecoverableDevTunnelFailure = activeTab
    ? isRecoverableDevTunnelFailure(activeTab)
    : false;
  const activeFrameIsFresh = activeBrowserFrameIsFresh({
    activeFrame: storeArtifacts.activeFrame,
    activeFrameMeta: storeArtifacts.activeFrameMeta,
    activeTab,
    session,
  });
  const activeScreenshotIsFresh = activeBrowserScreenshotIsFresh({
    activeScreenshot: storeArtifacts.activeScreenshot,
    activeScreenshotMeta: storeArtifacts.activeScreenshotMeta,
    activeTab,
    session,
  });
  const displayFrame = selectBrowserDisplayFrameSource({
    frameIsFresh: activeFrameIsFresh,
    frameDataUrl: storeArtifacts.activeFrame,
    frameUrl: storeArtifacts.activeFrameMeta?.url,
    frameCapturedAt: storeArtifacts.activeFrameMeta?.capturedAt,
    frameViewport: storeArtifacts.activeFrameMeta?.viewport,
    screenshotIsFresh: activeScreenshotIsFresh,
    screenshotDataUrl: storeArtifacts.activeScreenshot,
    screenshotUrl: storeArtifacts.activeScreenshotMeta?.url,
    screenshotCapturedAt: storeArtifacts.activeScreenshotMeta?.capturedAt,
    screenshotViewport: storeArtifacts.activeScreenshotMeta?.viewport,
  });

  return {
    activeFrameIsFresh,
    activeRenderMode,
    activeScreenshotIsFresh,
    activeTabDisplayUrl,
    activeTabHasRecoverableDevTunnelFailure,
    activeTabHasVisiblePreview: activeTabHasVisiblePreview({
      activeFrameIsFresh,
      activeScreenshotIsFresh,
      activeTab,
      activeTabHasRecoverableDevTunnelFailure,
      liveDisplayIsAttached,
    }),
    activeTabUrl,
    displayFrame,
    shouldPreferBrowserCapture,
    ...storeArtifacts,
  };
}

interface BrowserPreviewPanelSessionStateInput {
  readonly browserThreadState: BrowserThreadState | null | undefined;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
  readonly routeTabId?: string | null | undefined;
}

export function deriveBrowserPreviewPanelSessionState(input: BrowserPreviewPanelSessionStateInput) {
  const { browserThreadState, devTunnelRecords, routeTabId } = input;
  const session = browserThreadState?.session ?? null;
  const visibleTabs = visibleBrowserTabs(session?.tabs ?? [], devTunnelRecords);
  const visibleTabLabels = visibleTabs.map((tab, index) =>
    browserTabDisplayLabel(tab, index, devTunnelRecords),
  );
  const activeTab = selectPreferredPreviewTab({
    tabs: visibleTabs,
    routeTabId: routeTabId ?? null,
    activeTabId: session?.activeTabId ?? null,
  });
  const liveDisplayState = resolveBrowserLiveDisplayState({ session });
  const activeArtifacts = activeBrowserPreviewArtifacts({
    activeTab,
    browserThreadState,
    devTunnelRecords,
    liveDisplayIsAttached: liveDisplayState.isAttached,
    session,
  });

  return {
    activeTab,
    duplicateVisibleTabLabels: duplicateTabLabelSet(visibleTabLabels),
    recoverableDevPreviewUrl: recoverableLocalPreviewDisplayUrl(
      session?.tabs ?? [],
      devTunnelRecords,
    ),
    session,
    visibleSessionTabIdsKey: visibleTabs.map((tab) => tab.tabId).join("\0"),
    visibleTabLabels,
    visibleTabs,
    ...liveDisplayState,
    ...activeArtifacts,
  };
}

interface BrowserPreviewPanelRuntimeStateInput {
  readonly activeScreenshot: string | undefined;
  readonly activeScreenshotMeta:
    | BrowserThreadState["lastScreenshotMetaByTabId"][string]
    | undefined;
  readonly activeTab: BrowserTabSnapshot | null;
  readonly browserRuntimeError: string | null;
  readonly browserThreadState: BrowserThreadState | null | undefined;
  readonly connecting: boolean;
  readonly displayFrame: BrowserDisplayFrameSource;
  readonly hasBrowserRuntime: boolean;
  readonly lifecycleAction: string | null;
  readonly liveDisplayIsAttached: boolean;
  readonly openingBrowserSession: boolean;
  readonly pendingBrowserCommand: BrowserCommand["type"] | null;
  readonly session: BrowserSessionSnapshot | null;
}

export function deriveBrowserPreviewPanelRuntimeState(input: BrowserPreviewPanelRuntimeStateInput) {
  const visibleBrowserError =
    input.browserRuntimeError ??
    (input.session ? (input.browserThreadState?.lastError ?? null) : null);
  const blockingBrowserError = input.liveDisplayIsAttached ? null : visibleBrowserError;
  const busyState = resolveBrowserBusyState({
    connecting: input.connecting,
    openingBrowserSession: input.openingBrowserSession,
    lifecycleAction: input.lifecycleAction,
    pendingBrowserCommand: input.pendingBrowserCommand,
    hasSession: Boolean(input.session),
  });

  return {
    blockingBrowserError,
    browserAddressBusy: busyState.addressBusy,
    browserActionBusy: busyState.actionBusy,
    browserBusy: busyState.busy,
    browserCommandBusy: busyState.commandBusy,
    devServerLaunchBusy: busyState.actionBusy || (input.connecting && Boolean(input.session)),
    previewVm: resolveBrowserPreviewVm({
      hasBrowserRuntime: input.hasBrowserRuntime,
      runtimeError: blockingBrowserError,
      session: input.session,
      activeTab: input.activeTab,
      activeScreenshotDataUrl: input.activeScreenshot,
      activeScreenshotUrl: input.activeScreenshotMeta?.url,
      activeScreenshotCapturedAt: input.activeScreenshotMeta?.capturedAt,
      activeScreenshotViewport: input.activeScreenshotMeta?.viewport,
      activeFrameDataUrl: input.displayFrame.dataUrl,
      activeFrameUrl: input.displayFrame.url,
      activeFrameCapturedAt: input.displayFrame.capturedAt,
      activeFrameViewport: input.displayFrame.viewport,
      connecting: busyState.busy,
    }),
    visibleBrowserError,
  };
}

interface BrowserPreviewPanelInteractionStateInput {
  readonly activeRenderMode: BrowserRenderMode | null;
  readonly browserDisplay: BrowserDisplaySession | undefined;
  /** What this client can host. See `BrowserClientCapability`. */
  readonly client: BrowserClientCapability;
  readonly commentSelecting: boolean;
  readonly displayFrame: BrowserDisplayFrameSource;
  /** DOM layered over the panel, which an OS-level native view would cover. */
  readonly obscuredByHostOverlay: boolean;
  readonly previewInteractionMode: string | null | undefined;
  /** Whether the session has enough state to paint. The old `previewVm.canRenderCanvas`. */
  readonly canRenderSurface: boolean;
  readonly session: BrowserSessionSnapshot | null;
  readonly surfaceViewport: BrowserViewport | null;
}

export function deriveBrowserPreviewPanelInteractionState(
  input: BrowserPreviewPanelInteractionStateInput,
) {
  const annotateMode = input.previewInteractionMode === "annotate" || input.commentSelecting;
  const presentation = resolveBrowserPresentation({
    client: input.client,
    display: input.browserDisplay,
    renderMode: input.activeRenderMode,
    annotating: annotateMode,
    canRenderSurface: input.canRenderSurface,
    obscuredByHostOverlay: input.obscuredByHostOverlay,
  });

  return {
    annotateMode,
    presentation,
    interactiveMode: presentation.interactive,
    interactiveSurfaceViewport: resolveBrowserInteractiveSurfaceViewport({
      browserDisplay: input.browserDisplay,
      surfaceViewport: input.surfaceViewport ?? undefined,
      sessionViewport: input.session?.viewport ?? undefined,
      displayFrameViewport: input.displayFrame.viewport,
    }),
    remoteWebRtcSurfaceViewport:
      input.surfaceViewport ?? input.browserDisplay?.viewport ?? input.session?.viewport,
  };
}
